import { Box } from '@material-ui/core';
import React, { MouseEventHandler, useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { InformationModule, NamedLink, TypographyWrapper } from '..';
import {
  AboutPageTab,
  getSellerContentConfig,
  getTradeInContentConfig,
} from '../../containers/AboutPage/aboutPageUtils';
import { useShopConfig, useShopConfigV2 } from '../../hooks/shopConfig';
import { useShopCss } from '../../hooks/useShopCss';
import { useIsMobile } from '../../hooks/useIsMobile';
import { TypographyFormat } from '../TypographyWrapper/TypographyWrapper';
import { fetchSoldListings } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { ListingItemType, ListingWithAuthorAndImages } from '../../types/sharetribe/listing';
import { formatMoney } from '../../util/currency';
import { useFeaturedListingImage } from '../../hooks/images';
import './SellOrTradeInEducationSwiper.css';
import css from './SellOrTradeInEducation.module.css';
import AppContext from '../../context/AppContext';

const TradeInInfo = () => {
  const { shopName, images, buyerSellerInfoOverrides } = useShopConfigV2();
  const { treetId } = useContext(AppContext);
  const customIcons = images?.howItWorksCustomImages;
  const content = getTradeInContentConfig(
    shopName,
    treetId,
    customIcons,
    buyerSellerInfoOverrides,
    true
  );
  return <InformationModule content={content} contentContainerOverrides={{ mb: '12px' }} />;
};

const ListItemInfo = () => {
  const { buyerSellerInfoOverrides, cashPayoutPercentage, creditPayoutPercentage, images } =
    useShopConfigV2();
  const { treetId } = useContext(AppContext);

  const customIcons = images?.howItWorksCustomImages;

  const content = getSellerContentConfig(
    creditPayoutPercentage,
    cashPayoutPercentage,
    treetId,
    customIcons,
    buyerSellerInfoOverrides
  );

  return <InformationModule content={content} contentContainerOverrides={{ mb: '12px' }} />;
};

const SelectableTab = (props: {
  isSelected: boolean;
  title: string;
  subtitle: string;
  subsubtitle: string;
  onClick: MouseEventHandler;
}) => {
  const { isSelected, title, subtitle, subsubtitle, onClick } = props;
  const shopCss = useShopCss();
  const selectedHighlight = <Box bgcolor={shopCss.body1.color} height="3px" marginTop="8px" />;
  const selectedStyle = isSelected ? {} : { opacity: '0.6' };
  return (
    <Box
      className={css.selectableTab}
      textAlign="center"
      style={selectedStyle}
      px="10px"
      onClick={onClick}
    >
      <TypographyWrapper variant="h2" typographyOverrides={{ style: { fontWeight: 'bold' } }}>
        {title}
      </TypographyWrapper>
      <TypographyWrapper
        variant="body1"
        typographyOverrides={{ style: { fontWeight: 'bold', padding: '0 20px' } }}
      >
        {subtitle}
      </TypographyWrapper>
      <TypographyWrapper variant="body1">{subsubtitle}</TypographyWrapper>
      {isSelected && selectedHighlight}
    </Box>
  );
};

const ListAndTradeInContent = () => {
  enum TabContent {
    TradeIn = 'tradeIn',
    Sell = 'sell',
  }
  const [visibleTab, setVisibleTab] = useState(TabContent.Sell);
  return (
    <>
      <Box
        display="grid"
        gridTemplateColumns="1fr"
        gridTemplateRows="1fr min-content"
        gridGap="20px 0px"
        maxWidth="450px"
      >
        <Box
          width="100%"
          display="flex"
          justifyContent="space-evenly"
          alignContent="flex-start"
          gridColumn="1"
          gridRow="1"
        >
          <SelectableTab
            title="List For Sale"
            subtitle="Earn More"
            subsubtitle="(Cash or Credit)"
            isSelected={visibleTab === TabContent.Sell}
            onClick={() => setVisibleTab(TabContent.Sell)}
          />
          <SelectableTab
            title="Trade In"
            subtitle="Fast Payout"
            subsubtitle="(Credit)"
            isSelected={visibleTab === TabContent.TradeIn}
            onClick={() => setVisibleTab(TabContent.TradeIn)}
          />
        </Box>
        <Box alignSelf="center" justifySelf="center" gridColumn="1" gridRow="2">
          {visibleTab === TabContent.TradeIn && <TradeInInfo />}
          {visibleTab === TabContent.Sell && <ListItemInfo />}
        </Box>
      </Box>
    </>
  );
};

const SoldListingCard = (params: { listing: ListingWithAuthorAndImages }) => {
  const { listing } = params;
  const {
    attributes: { price },
  } = listing;
  const { treetShopName, styles } = useShopConfig();
  const intl = useIntl();
  const featuredListingImage = useFeaturedListingImage(listing);
  const formattedPrice = formatMoney(intl, price);
  return (
    <Box
      minWidth="200px"
      maxWidth="250px"
      height="min-content"
      width="min-content"
      display="grid"
      bgcolor="white"
      style={{
        placeItems: 'center',
        placeContent: 'center',
        gridTemplate: '"container"',
        overflow: 'hidden',
      }}
      border={`1px solid ${styles.gray60}`}
      boxShadow="rgba(0,0,0,0.2) 5px 2px 6px"
    >
      <img
        style={{
          opacity: 0.2,
          gridArea: 'container',
          position: 'relative',
          zIndex: 0,
          maxHeight: '320px',
        }}
        width="auto"
        alt="listing"
        src={featuredListingImage.src}
      />
      <Box
        textAlign="center"
        width="100%"
        gridArea="container"
        style={{ position: 'relative', zIndex: 1 }}
      >
        <TypographyWrapper variant="h2">SOLD</TypographyWrapper>
        <TypographyWrapper variant="body2" typographyOverrides={{ style: { margin: '5px 0' } }}>
          on {treetShopName}
        </TypographyWrapper>
        <TypographyWrapper variant="h2" typographyOverrides={{ style: { fontSize: '42px' } }}>
          {formattedPrice}
        </TypographyWrapper>
      </Box>
    </Box>
  );
};

const ListingsSwipeStack = (params: { soldListings: ListingWithAuthorAndImages[] }) => {
  const [swiperLibrary, setSwiperLibrary] = useState<any>(undefined);
  const [swiperReactLibrary, setSwiperReactLibrary] = useState<any>(undefined);

  useEffect(() => {
    const loadData = async () => {
      // Load this in asynchronously
      // because for some reason, it doesn't play well with SSR
      const SwiperCore = await import('swiper');
      const SwiperReact = await import('swiper/react');
      const SwiperStyles = await import('./swiperStylesUtils');
      await SwiperStyles.default();
      setSwiperLibrary(SwiperCore);
      setSwiperReactLibrary(SwiperReact);
    };

    loadData();
  }, []);

  if (!swiperLibrary || !swiperReactLibrary) return null;

  const { EffectCards, Autoplay } = swiperLibrary;
  const { Swiper, SwiperSlide } = swiperReactLibrary;

  const { soldListings } = params;
  if (isEmpty(soldListings)) {
    return null;
  }
  return (
    <Swiper
      modules={[EffectCards, Autoplay]}
      autoplay={{ delay: 3000, stopOnLastSlide: true }}
      effect="cards"
      grabCursor
      slideToClickedSlide
      cardsEffect={{ slideShadows: false, perSlideOffset: 15 }}
    >
      {soldListings.map((listing) => (
        <SwiperSlide key={listing.id.uuid} style={{ overflow: 'visible' }}>
          {({ isActive, isPrev, isNext }: any) => {
            const activeSlideStyles = isActive
              ? { transform: 'translateY(-80px)', opacity: 1 }
              : {};
            const prevNextSlideStyles =
              isPrev || isNext ? { transform: 'translateY(-40px)', opacity: 0.8 } : {};
            return (
              <Box
                style={{
                  opacity: 0.5,
                  ...activeSlideStyles,
                  ...prevNextSlideStyles,
                }}
              >
                <SoldListingCard listing={listing} />
              </Box>
            );
          }}
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

const SellOrTradeInEducation = () => {
  const isMobile = useIsMobile();
  const [soldListings, setSoldListings] = useState([] as ListingWithAuthorAndImages[]);

  useEffect(() => {
    fetchSoldListings({
      page: 1,
      perPage: 3,
      sort: 'pub_soldAt',
      params: { pub_listingItemType: ListingItemType.TradeIn, price: '100,' },
    }).then((response: any) => {
      const listings = denormalisedResponseEntities(response);
      setSoldListings(listings);
    });
  }, []);

  return (
    <Box
      display="grid"
      gridTemplateColumns={isMobile ? '100%' : 'minmax(50%, 600px) min-content'}
      gridTemplateRows={isMobile ? 'min-content min-content min-content' : 'min-content 1fr'}
      gridGap="15px 40px"
      justifyItems="center"
      alignItems="center"
      width="100%"
      maxHeight="100%"
      justifyContent="center"
    >
      <Box
        sx={{
          gridRow: '1',
          gridColumn: isMobile ? '1' : 'span 2',
          textAlign: 'center',
        }}
      >
        <TypographyWrapper
          variant="h1"
          typographyOverrides={{ style: { fontSize: isMobile ? '24px' : '42px' } }}
        >
          2 flexible ways to extend
          <br />
          the life of your item.
        </TypographyWrapper>
      </Box>
      <Box gridColumn="1" gridRow="2">
        <Box padding="30px">
          <ListAndTradeInContent />
        </Box>
        <Box pb="20px">
          <NamedLink name="AboutPage" params={{ tab: AboutPageTab.Seller }}>
            <TypographyWrapper
              variant="body1"
              format={TypographyFormat.Underlined}
              typographyOverrides={{ style: { textAlign: 'center' } }}
            >
              Learn more about selling or trading in on Treet.
            </TypographyWrapper>
          </NamedLink>
        </Box>
      </Box>
      <Box gridColumn={isMobile ? '1' : '2'} gridRow={isMobile ? '3' : '2'}>
        {!isMobile && (
          <Box className="listingSwipe" maxWidth="300px" style={{ transform: 'rotate(6deg)' }}>
            <ListingsSwipeStack soldListings={soldListings} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SellOrTradeInEducation;
